import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { CustomTextInput } from "./inputFields/customTextInput";

export type MultiOptionItem = { name: string };

function itemsFromProp(value: unknown): MultiOptionItem[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    if (item !== null && typeof item === "object" && "name" in item) {
      return { name: String((item as MultiOptionItem).name ?? "") };
    }
    return { name: String(item ?? "") };
  });
}

function payloadFromItems(items: MultiOptionItem[]): MultiOptionItem[] | null {
  if (items.length === 0) return null;
  if (items.every((row) => row.name === "")) return null;
  return items.map((row) => ({ name: row.name }));
}

function payloadsEqual(
  a: MultiOptionItem[] | null | undefined,
  b: MultiOptionItem[] | null | undefined,
): boolean {
  const norm = (x: MultiOptionItem[] | null | undefined) =>
    (x ?? []).map((r) => r.name);
  const aa = norm(a);
  const bb = norm(b);
  if (aa.length !== bb.length) return false;
  return aa.every((v, i) => v === bb[i]);
}

interface MultiOptionFieldProps {
  field: string;
  label: string;
  value: unknown;
  onChange: (field: string, value: MultiOptionItem[] | null) => void;
}

export const MultiOptionField = memo(function MultiOptionField({
  field,
  label,
  value,
  onChange,
}: MultiOptionFieldProps) {
  const [items, setItems] = useState<MultiOptionItem[]>([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    // Skip the echo of our own commit so local-only rows (e.g. a blank row
    // just added, or one being cleared) are not collapsed while typing.
    const incoming = itemsFromProp(value);
    if (payloadsEqual(payloadFromItems(itemsRef.current), payloadFromItems(incoming))) {
      return;
    }
    setItems(incoming);
  }, [value]);

  const rowCount = Math.max(1, items.length);

  // Commits on every keystroke so Save sees the edit without a blur
  const setNameAt = useCallback(
    (index: number, name: string) => {
      const next = [...itemsRef.current];
      while (next.length <= index) {
        next.push({ name: "" });
      }
      next[index] = { name };
      itemsRef.current = next;
      setItems(next);

      const payload = payloadFromItems(next);
      if (!payloadsEqual(payload, payloadFromItems(itemsFromProp(value)))) {
        onChange(field, payload);
      }
    },
    [field, onChange, value],
  );

  const addRow = useCallback(() => {
    setItems((prev) => {
      if (prev.length === 0) {
        return [{ name: "" }, { name: "" }];
      }
      return [...prev, { name: "" }];
    });
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
      {Array.from({ length: rowCount }, (_, index) => {
        const name = items[index]?.name ?? "";
        const rowLabel = index === 0 ? label : `${label} (${index + 1})`;

        return (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              width: "100%",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CustomTextInput
                label={rowLabel}
                type="text"
                value={name}
                onChange={(val) => setNameAt(index, String(val))}
                fullWidth
              />
            </Box>
            <IconButton
              type="button"
              aria-label="Add another"
              onClick={addRow}
              size="small"
              sx={{ flexShrink: 0 }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      })}
    </Box>
  );
});
